import { Button, H5, H6 } from "@sellar-io/components";
import { ProductListResponseType } from "@sellar-io/api-v2/types";
import {
  getBadgeDisplayName,
  getProductDisplayNameByKey,
  pluralize,
} from "@sellar-io/common";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "use-debounce";
import { Icon, SpinnerLegacy, StockIndicator } from "../../../../components";
import { DietaryDisplayList } from "../../../../components/dietaryDisplayList/DietaryDisplayList";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogPage,
} from "../../../../components/modal/Dialog";
import { useFullPageDialogContext } from "../../../../components/modal/useFullPageDialogContext";
import { useProductList } from "../../../../hooks/api/v2/product/useProductList";
import {
  FilterBadge,
  FilterSwitch,
} from "../../../discovery/v2/filterCards/FilterCard";
import { OrderItemForm } from "../forms/OrderItemForm";
import {
  OrderOnBehalfOfOrderItemType,
  useOrderOnBehalfOfContext,
} from "../OrderOnBehalfOf.shared";
import { AvailableFromBadge } from "../../../../components/badge/AvailableFromBadge";
import {
  AddToOrderDialogPage,
  AddToOrderDialogContext,
  useAddToOrderDialogContext,
  containerFilterList,
  statusFilterList,
  containerNameAsValue,
} from "./AddToOrderDialog.shared";

export const AddToOrderDialog = ({
  active,
  onDismiss,
  onSubmit,
}: {
  active: boolean;
  onDismiss: () => void;
  onSubmit: (product: OrderOnBehalfOfOrderItemType) => void;
}) => {
  return (
    <Dialog
      fullHeight={true}
      active={active}
      onDismiss={onDismiss}
      initPage={AddToOrderDialogPage.PRODUCT_LIST}
    >
      <AddToOrderDialogContent onSubmit={onSubmit} />
    </Dialog>
  );
};

const AddToOrderDialogContent = ({
  onSubmit,
}: {
  onSubmit: (orderItem: OrderOnBehalfOfOrderItemType) => void;
}) => {
  const { setPage } = useFullPageDialogContext();

  const [productItem, setProductItem] =
    useState<ProductListResponseType[number]>();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<
    StatusFilter[]
  >(["In stock", "Active only"]);
  const [selectedContainerFilters, setSelectedContainerFilters] = useState<
    ContainerFilter[]
  >([]);

  useEffect(() => {
    return () => {
      setPage(AddToOrderDialogPage.PRODUCT_LIST);
    };
  }, [setPage]);

  return (
    <AddToOrderDialogContext.Provider
      value={{
        productItem,
        setProductItem,
        searchQuery,
        setSearchQuery,
        selectedStatusFilters,
        setSelectedStatusFilters,
        selectedContainerFilters,
        setSelectedContainerFilters,
        onSubmit: (product) => {
          onSubmit(product);
        },
      }}
    >
      <DialogPage page={AddToOrderDialogPage.PRODUCT_LIST}>
        <ProductListV2 />
      </DialogPage>

      <DialogPage page={AddToOrderDialogPage.PRODUCT_ITEM}>
        <ProductItemPageContent />
      </DialogPage>
    </AddToOrderDialogContext.Provider>
  );
};
const ProductListElementV2 = ({
  product,
  onClick,
}: {
  product: ProductListResponseType[number];
  onClick: () => void;
}) => {
  const { orderItems, setEditingProductId } = useOrderOnBehalfOfContext();
  const { onDismiss } = useFullPageDialogContext();
  const productAlreadyAdded = orderItems.some(
    (orderItem) => orderItem.Product.id === product.id,
  );

  const renderProductItemRow = (product: ProductListResponseType[number]) => {
    const productContainerDisplayName = product.containerType
      ? pluralize(
          getProductDisplayNameByKey(product.containerType) ?? "",
          product.packQuantity ?? 1,
        )
      : null;

    return (
      <>
        {product.packQuantity && product.packQuantity > 1 ? (
          <div>
            {product.packQuantity}x {product.volume}
            {product.unit} {productContainerDisplayName}{" "}
            <DietaryDisplayList
              dietaryRequirements={{
                VG: Boolean(product.vegan),
                GF: Boolean(product.glutenFree),
                LF: Boolean(product.lactoseFree),
              }}
              mode="text"
            />
          </div>
        ) : (
          <div>
            {product.volume}
            {product.unit} {productContainerDisplayName}{" "}
            <DietaryDisplayList
              dietaryRequirements={{
                VG: Boolean(product.vegan),
                GF: Boolean(product.glutenFree),
                LF: Boolean(product.lactoseFree),
              }}
              mode="text"
            />
          </div>
        )}
      </>
    );
  };

  const renderBadgeRow = (product: ProductListResponseType[number]) => {
    const badgeDisplayName = getBadgeDisplayName(product.shape ?? "");
    return <div>{badgeDisplayName}</div>;
  };

  const isProductItemActive = product.active && product.Parent?.active;

  return (
    <div
      className={twMerge(
        "flex cursor-pointer items-center justify-between overflow-hidden border-b border-solid border-b-zinc-200 bg-white p-4 transition-colors hover:bg-zinc-50",
        !isProductItemActive && "bg-zinc-50",
      )}
      onClick={
        productAlreadyAdded
          ? () => {
              setEditingProductId(product.id);
              onDismiss?.();
            }
          : onClick
      }
    >
      <div>
        <div className="flex items-center gap-2">
          <H5>{product.Parent?.name}</H5>
          {!isProductItemActive && (
            <span className="text-xs font-semibold text-zinc-500">
              INACTIVE
            </span>
          )}
        </div>
        <H6>
          {product.Parent?.kind}
          {" // "}
          {product.Parent?.abv}%
        </H6>

        {product.type === "productItem" && renderProductItemRow(product)}
        {product.type === "badge" && renderBadgeRow(product)}
        {!!product.availableFrom && (
          <AvailableFromBadge availableFrom={product.availableFrom} />
        )}
      </div>

      <div className="flex items-center justify-between gap-4">
        <StockIndicator
          stock={product.availableStock}
          canBackorder={product.canBackorder && product.availableStock < 1}
          type={"short"}
        />
        <Icon icon={"PLUS"} />
      </div>
    </div>
  );
};

type ContainerFilter = (typeof containerFilterList)[number];
type StatusFilter = (typeof statusFilterList)[number];

const SearchCard = ({
  setSearchQuery,
  searchQuery,
}: {
  searchQuery: string;
  setSearchQuery: (arg: string) => void;
}) => {
  return (
    <InputContainer>
      <InputText
        autoFocus
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={"Search for a product..."}
      />
    </InputContainer>
  );
};

const StatusFilterCard = ({
  defaultState,
  onStatusFilterChanged,
}: {
  defaultState?: StatusFilter[];
  onStatusFilterChanged: (selectedFilterOptions: StatusFilter[]) => void;
}) => {
  const [selectedStatusFilterList, setSelectedStatusFilterList] = useState<
    StatusFilter[]
  >(defaultState ?? []);
  useEffect(() => {
    onStatusFilterChanged(selectedStatusFilterList);
  }, [onStatusFilterChanged, selectedStatusFilterList]);

  return (
    <div className="flex flex-col gap-1 pt-4">
      {statusFilterList.map((statusFilter) => {
        return (
          <FilterSwitch
            key={statusFilter}
            title={statusFilter}
            checked={selectedStatusFilterList.includes(statusFilter)}
            onChange={() => {
              setSelectedStatusFilterList((currentStatusFilterList) => {
                // remove filter option from selected filter option list
                if (currentStatusFilterList.includes(statusFilter)) {
                  return currentStatusFilterList.filter(
                    (option) => option !== statusFilter,
                  );
                }

                // add filter option to selected filter option list
                return [...currentStatusFilterList, statusFilter];
              });
            }}
          />
        );
      })}
    </div>
  );
};

const ContainerFilterCard = ({
  defaultState,
  onContainerFilterChanged,
}: {
  defaultState?: ContainerFilter[];
  onContainerFilterChanged: (
    selectedContainerOptions: ContainerFilter[],
  ) => void;
}) => {
  const [selectedContainerFilterList, setSelectedContainerFilterList] =
    useState<ContainerFilter[]>(defaultState ?? []);

  useEffect(() => {
    onContainerFilterChanged(selectedContainerFilterList);
  }, [onContainerFilterChanged, selectedContainerFilterList]);

  return (
    <div className="flex flex-wrap gap-1 pt-4">
      {containerFilterList.map((containerFilter) => {
        return (
          <FilterBadge
            key={containerFilter}
            selected={selectedContainerFilterList.includes(containerFilter)}
            onClick={() => {
              setSelectedContainerFilterList((currentStatusFilterList) => {
                // remove container from selected filter option list
                if (currentStatusFilterList.includes(containerFilter)) {
                  return currentStatusFilterList.filter(
                    (option) => option !== containerFilter,
                  );
                }

                // add container to selected filter option list
                return [...currentStatusFilterList, containerFilter];
              });
            }}
          >
            {containerFilter}
          </FilterBadge>
        );
      })}
    </div>
  );
};

const ProductListV2 = () => {
  const { connection } = useOrderOnBehalfOfContext();
  const { onDismiss, setPage } = useFullPageDialogContext();
  const {
    setProductItem,
    searchQuery,
    setSearchQuery,
    setSelectedStatusFilters,
    setSelectedContainerFilters,
    selectedContainerFilters,
    selectedStatusFilters,
  } = useAddToOrderDialogContext();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const productListQuery = useProductList(
    {
      search: debouncedSearchQuery,
      sortBy: "name",
      sortDir: "asc",
      query: [
        {
          type: "Products",
          modifier: "IS_NULL",
        },
        { type: "ParentId", modifier: "NOT_NULL" },
        {
          type: "showAllProducts",
          modifier: "EQUALS",
          value: true,
        },
        ...(selectedStatusFilters.includes("Active only")
          ? [
              {
                type: "active",
                modifier: "EQUALS",
                value: true,
              } as const,
            ]
          : []),
        ...(selectedStatusFilters.includes("In stock")
          ? [
              {
                type: "availableStock",
                modifier: "GREATER_THAN_OR_EQUAL",
                value: 1,
              } as const,
            ]
          : []),

        ...(selectedContainerFilters.length
          ? ([
              {
                type: "containerType",
                modifier: "IN",
                value: selectedContainerFilters.map((containerName) =>
                  containerNameAsValue(containerName),
                ),
              },
            ] as const)
          : []),
      ],
    },
    {
      RetailerId: connection.BuyerId ?? 0,
    },
  );

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Add line item"}
      >
        <SearchCard searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        <StatusFilterCard
          defaultState={selectedStatusFilters}
          onStatusFilterChanged={(selectedFilterOptionList) =>
            setSelectedStatusFilters(selectedFilterOptionList)
          }
        />
        <ContainerFilterCard
          defaultState={selectedContainerFilters}
          onContainerFilterChanged={(selectedContainerOptionList) => {
            setSelectedContainerFilters(selectedContainerOptionList);
          }}
        />
      </DialogNavigation>
      <DialogContent className={"pb-4"}>
        {productListQuery.isLoading ? (
          <SpinnerLegacy />
        ) : !productListQuery.data?.pages[0]?.[0] ? (
          <div>No products found</div>
        ) : (
          <InfiniteList
            query={productListQuery}
            className={"divide-y rounded-lg border border-solid"}
          >
            {productListQuery.data.pages.map((page) =>
              page.map((product) => (
                <ProductListElementV2
                  key={product.id}
                  product={product}
                  onClick={() => {
                    setProductItem({ ...product, name: product.name ?? "" });
                    setPage(AddToOrderDialogPage.PRODUCT_ITEM);
                  }}
                />
              )),
            )}
          </InfiniteList>
        )}
      </DialogContent>
    </>
  );
};

export const ProductItemPageContent = () => {
  const { setPage } = useFullPageDialogContext();
  const { productItem, setProductItem, onSubmit } =
    useAddToOrderDialogContext();

  if (!productItem) throw new Error("No product item selected.");

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => setPage(AddToOrderDialogPage.PRODUCT_LIST)}
        title={"Add line item"}
      />
      <DialogContent>
        <OrderItemForm
          onRemoveItem={() => {
            setPage(AddToOrderDialogPage.PRODUCT_LIST);
            setProductItem(undefined);
          }}
          product={{ ...productItem, name: productItem.name ?? "" }}
          onSubmit={onSubmit}
        >
          <Button className={"w-full"} type={"submit"}>
            Add item
          </Button>
        </OrderItemForm>
      </DialogContent>
    </>
  );
};
